import { useMemo } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

/**
 * Hook for checking Statsig feature gates (feature flags)
 * Use this for client-side feature rollouts, A/B tests, and gradual releases
 *
 * IMPORTANT: This is different from useFeatureFlag in @features/user
 * - useFeatureGate: Statsig-managed (this hook)
 * - useFeatureFlag: Backend-managed (from @features/user)
 *
 * NOTE: Statsig automatically logs exposure events when you call checkGate(),
 * so you get analytics tracking for free!
 *
 * @param gateName - The Statsig feature gate name (e.g., 'feature_edit_template_from_note')
 * @returns boolean indicating if the gate is passing for the current user
 *
 * @example
 * const showNewUI = useFeatureGate('feature_new_ui');
 * return showNewUI ? <NewUI /> : <OldUI />;
 */
export function useFeatureGate(gateName: string): boolean {
  const { client } = useStatsigClient();

  // useMemo prevents duplicate exposure logging on re-renders
  // checkGate automatically logs exposure events to Statsig
  return useMemo(() => {
    return client.checkGate(gateName);
  }, [client, gateName]);
}

/**
 * Hook for checking multiple Statsig feature gates at once
 * Returns an object with gate names as keys and boolean values
 *
 * All gates are checked and exposures are automatically logged.
 *
 * @param gateNames - Array of Statsig feature gate names
 * @returns Object mapping gate names to their boolean values
 *
 * @example
 * const gates = useFeatureGates(['feature_a', 'feature_b']);
 * if (gates.feature_a && gates.feature_b) {
 *   // Both enabled
 * }
 */
export function useFeatureGates(gateNames: string[]): Record<string, boolean> {
  const { client } = useStatsigClient();

  // Memoize to prevent duplicate exposure logging on re-renders
  // Optimization: use stringified key to avoid recalculation on array reference changes
  const gateNamesKey = useMemo(() => gateNames.join(','), [gateNames]);

  return useMemo(() => {
    // Parse the key back to array for gate checking
    const names = gateNamesKey.split(',').filter(Boolean);
    return names.reduce<Record<string, boolean>>((acc, gateName) => {
      // Each checkGate call automatically logs exposure
      acc[gateName] = client.checkGate(gateName);
      return acc;
    }, {});
  }, [client, gateNamesKey]);
}
